import { Controller } from "@hotwired/stimulus"
import {
  PANEL_TRANSITION_MS,
  panelClosePlan,
  panelPresentation,
  panelHistoryAction,
  panelTranslation,
  taskContextRestoration,
  taskContextUrl,
  taskIdFromRecordUrl
} from "../lib/slide_panel_state.mjs"

/**
 * Universal record workspace.
 *
 * The controller is mounted on the authenticated application shell so task,
 * deal, ticket, and project links can open the shared panel from anywhere in
 * the workspace. The panel is non-modal on desktop and full-screen on mobile.
 */
export default class extends Controller {
  static targets = ["container", "backdrop", "panel", "title", "body", "fullLink", "closeButton"]

  connect() {
    this.boundHandleOpen = this.handleOpen.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    this.boundPopstate = this.onPopstate.bind(this)
    this.boundResize = this.syncPresentation.bind(this)

    document.addEventListener("slide-panel:open", this.boundHandleOpen)
    document.addEventListener("task-drawer:open", this.boundHandleOpen)
    document.addEventListener("keydown", this.boundKeydown)
    window.addEventListener("popstate", this.boundPopstate)
    window.addEventListener("resize", this.boundResize)
    const restoration = taskContextRestoration(window.location.href)
    if (restoration) {
      this.restoreContextualTask(restoration)
    } else if (!window.history.state?.slidePanel) {
      this.resetClosedDrawer()
    }
    this.syncPresentation()
  }

  disconnect() {
    document.removeEventListener("slide-panel:open", this.boundHandleOpen)
    document.removeEventListener("task-drawer:open", this.boundHandleOpen)
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("popstate", this.boundPopstate)
    window.removeEventListener("resize", this.boundResize)
    this.contentRequest?.abort()
    window.clearTimeout(this.closeTimer)
    window.clearTimeout(this.historyTimer)
    document.body.style.overflow = ""
  }

  handleOpen(event) {
    const { url, title } = event.detail
    if (url) this.openDrawer(url, title || "Details")
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = event.params?.url ||
                event.currentTarget.dataset.slidePanelUrl ||
                event.currentTarget.dataset.taskUrl ||
                event.currentTarget.getAttribute("href")
    const title = event.params?.title ||
                  event.currentTarget.dataset.slidePanelTitle ||
                  event.currentTarget.dataset.taskTitle ||
                  "Details"

    if (url) this.openDrawer(url, title)
  }

  openDrawer(url, title, options = {}) {
    const alreadyOpen = this.isOpen()
    window.clearTimeout(this.closeTimer)

    if (!alreadyOpen) {
      this.previousUrl = options.previousUrl || window.location.href
      this.previousFocus = options.restore ? null : document.activeElement
    }

    this.currentUrl = url
    this.titleTarget.textContent = title
    this.fullLinkTarget.href = url
    this.containerTarget.classList.remove("hidden")
    this.containerTarget.setAttribute("aria-hidden", "false")
    this.bodyTarget.innerHTML = this.loadingHTML()
    this.syncPresentation()

    requestAnimationFrame(() => {
      this.backdropTarget.style.opacity = "1"
      // Tailwind 4 implements translate utilities with the individual
      // `translate` property, so changing `transform` does not override the
      // initial `translate-x-full` position.
      this.panelTarget.style.translate = panelTranslation(true)
      this.closeButtonTarget.focus({ preventScroll: true })
    })

    this.loadContent(url)

    const taskId = taskIdFromRecordUrl(url, window.location.href)
    this.syncContextLinks(taskId)

    if (options.history !== false && window.history.pushState) {
      const action = panelHistoryAction(alreadyOpen)
      const historyUrl = taskContextUrl(this.previousUrl, url)
      window.history[action](
        { slidePanel: true, slidePanelReturnUrl: this.previousUrl, slidePanelCloseMode: "back" },
        "",
        historyUrl
      )
    }
  }

  restoreContextualTask(restoration) {
    window.history.replaceState(
      {
        slidePanel: true,
        slidePanelReturnUrl: restoration.returnUrl,
        slidePanelCloseMode: "replace"
      },
      "",
      window.location.href
    )
    this.openDrawer(restoration.recordUrl, "Task details", {
      history: false,
      previousUrl: restoration.returnUrl,
      restore: true
    })
  }

  close() {
    const plan = panelClosePlan(window.history.state)
    this.hideDrawer()
    if (plan.navigateBack) {
      this.historyTimer = window.setTimeout(() => window.history.back(), PANEL_TRANSITION_MS)
    } else if (plan.replaceUrl) {
      window.history.replaceState({}, "", plan.replaceUrl)
    }
  }

  hideDrawer() {
    window.clearTimeout(this.closeTimer)
    window.clearTimeout(this.historyTimer)
    this.contentRequest?.abort()
    this.panelTarget.style.translate = panelTranslation(false)
    this.backdropTarget.style.opacity = "0"
    document.body.style.overflow = ""

    this.closeTimer = window.setTimeout(() => {
      this.containerTarget.classList.add("hidden")
      this.containerTarget.setAttribute("aria-hidden", "true")
      this.bodyTarget.innerHTML = this.loadingHTML()
      this.currentUrl = null
    }, PANEL_TRANSITION_MS)

    if (this.previousFocus?.isConnected) {
      this.previousFocus.focus({ preventScroll: true })
    }
    this.syncContextLinks(null)
    this.previousFocus = null
    this.previousUrl = null
  }

  resetClosedDrawer() {
    window.clearTimeout(this.closeTimer)
    window.clearTimeout(this.historyTimer)
    this.containerTarget.classList.add("hidden")
    this.containerTarget.setAttribute("aria-hidden", "true")
    this.panelTarget.style.translate = panelTranslation(false)
    this.backdropTarget.style.opacity = "0"
    document.body.style.overflow = ""
    this.currentUrl = null
    this.syncContextLinks(null)
  }

  onKeydown(event) {
    if (!this.isOpen()) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key === "Tab" && panelPresentation(window.innerWidth).modal) {
      this.keepFocusInPanel(event)
    }
  }

  onPopstate() {
    const restoration = taskContextRestoration(window.location.href)
    if (restoration) {
      if (!this.isOpen() || this.currentUrl !== restoration.recordUrl) {
        this.openDrawer(restoration.recordUrl, "Task details", {
          history: false,
          previousUrl: restoration.returnUrl,
          restore: true
        })
      }
    } else if (this.isOpen()) {
      this.hideDrawer()
    }
  }

  isOpen() {
    return !this.containerTarget.classList.contains("hidden")
  }

  syncPresentation() {
    const presentation = panelPresentation(window.innerWidth)
    this.panelTarget.setAttribute("aria-modal", String(presentation.modal))
    this.backdropTarget.classList.toggle("hidden", !presentation.modal)
    this.containerTarget.classList.toggle("pointer-events-none", !presentation.modal)
    document.body.style.overflow = presentation.lockScroll && this.isOpen() ? "hidden" : ""
  }

  keepFocusInPanel(event) {
    const focusable = Array.from(this.panelTarget.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.offsetParent !== null)
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  syncContextLinks(taskId) {
    document.querySelectorAll("[data-slide-panel-context-link]").forEach((link) => {
      try {
        const url = new URL(link.href, window.location.href)
        if (taskId) url.searchParams.set("task", taskId)
        else url.searchParams.delete("task")
        link.href = url.toString()
      } catch (_) {
        // Leave malformed or non-HTTP links untouched.
      }
    })
  }

  async loadContent(url) {
    this.contentRequest?.abort()
    this.contentRequest = new AbortController()
    const request = this.contentRequest

    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest",
          "X-Slide-Panel": "true"
        },
        signal: request.signal
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      if (request !== this.contentRequest) return

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const drawerContent = doc.querySelector("[data-slide-panel-content]")

      if (drawerContent) {
        this.bodyTarget.innerHTML = drawerContent.innerHTML
      } else {
        const mainContent = doc.querySelector(".lg\\:col-span-2") || doc.querySelector("main") || doc.body
        this.bodyTarget.innerHTML = `<div class="p-4 sm:p-6 space-y-6">${mainContent.innerHTML}</div>`
      }

      const panelTitle = doc.querySelector("[data-slide-panel-title]")
      const heading = doc.querySelector("h1")
      if (panelTitle) this.titleTarget.textContent = panelTitle.dataset.slidePanelTitle || panelTitle.textContent.trim()
      else if (heading) this.titleTarget.textContent = heading.textContent.trim()

      this.bodyTarget.querySelectorAll("a[data-slide-panel-link]").forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault()
          event.stopPropagation()
          this.openDrawer(link.href, link.dataset.slidePanelTitle || link.textContent.trim() || "Details")
        })
      })
    } catch (error) {
      if (error.name === "AbortError") return

      console.error("Slide panel: failed to load content", error)
      this.bodyTarget.innerHTML = this.errorHTML()
    }
  }

  retry() {
    if (this.currentUrl) {
      this.bodyTarget.innerHTML = this.loadingHTML()
      this.loadContent(this.currentUrl)
    }
  }

  loadingHTML() {
    return `
      <div class="p-6" role="status" aria-live="polite">
        <div class="flex items-center justify-center py-16">
          <div class="flex flex-col items-center gap-3">
            <div class="animate-spin h-7 w-7 border-2 border-mod-warm-300 dark:border-white/10 border-t-mod-charcoal dark:border-t-white rounded-full"></div>
            <span class="text-xs text-mod-text-muted">Loading details…</span>
          </div>
        </div>
      </div>
    `
  }

  errorHTML() {
    return `
      <div class="p-8 text-center" role="alert">
        <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
          <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        </div>
        <p class="text-sm font-medium text-mod-charcoal dark:text-white mb-1">Could not load these details</p>
        <p class="text-xs text-mod-text-muted mb-4">Your workspace is unchanged. Try again or use the full-page link above.</p>
        <button type="button" data-action="click->slide-panel#retry" class="mod-btn mod-btn--secondary text-sm">Try again</button>
      </div>
    `
  }
}
